import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import AfterDateFilter from 'components/brokers/needs_attention/AfterDateFilter'
import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import BeforeDateFilter from 'components/brokers/needs_attention/BeforeDateFilter'
import BrokerageFilter from 'components/brokers/needs_attention/BrokerageFilter'
import CarrierNameFilter from 'components/brokers/needs_attention/CarrierNameFilter'
import EmployerFilter from 'components/brokers/needs_attention/EmployerFilter'
import EnrollmentCategoryFilter from 'components/brokers/needs_attention/EnrollmentCategoryFilter'
import MemberFilter from 'components/brokers/needs_attention/MemberFilter'
import TasksStatusFilter from 'components/brokers/tasks/TasksStatusFilter'
import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import CoverageTypeFilter from 'components/brokers/workflows/CoverageTypeFilter'
import DeadlineDateAfterFilter from 'components/brokers/workflows/DeadlineDateAfterFilter'
import DeadlineDateBeforeFilter from 'components/brokers/workflows/DeadlineDateBeforeFilter'
import EmployerReviewStatusFilter from 'components/brokers/workflows/EmployerReviewStatusFilter'
import EnrollmentEventTypeFilter from 'components/brokers/workflows/EnrollmentEventTypeFilter'
import SourceFilter from 'components/brokers/workflows/SourceFilter'
import WorkflowsStatusFilter from 'components/brokers/workflows/WorkflowsStatusFilter'
import WorkflowsTypeFilter from 'components/brokers/workflows/WorkflowsTypeFilter'
import WorkflowTaskCategoryFilter from 'components/brokers/workflows/WorkflowTaskCategoryFilter'
import WorkflowTaskExecutionContextFilter from 'components/brokers/workflows/WorkflowTaskExecutionContextFilter'
import WorkflowTaskExecutionMethodFilter from 'components/brokers/workflows/WorkflowTaskExecutionMethodFilter'
import WorkflowTaskTypeFilter from 'components/brokers/workflows/WorkflowTaskTypeFilter'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

import { type WorkflowsFilters } from './workflowsHelpers'

export type WorkflowFilterGroupType = 'Date' | 'Workflow' | 'Associations' | 'Triaging' | 'Next Task' | 'Task Filters'

type SingleSelectFilterComponents = Record<
  string,
  FC<SingleSelectedValueFilter>
>
type MultiSelectFilterComponents = Record<
  string,
  FC<MultipleSelectedValuesFilter>
>

export const workflowsSingleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
  deadline_date_after: DeadlineDateAfterFilter,
  deadline_date_before: DeadlineDateBeforeFilter,
}

export const workflowsMultiSelectFilters: MultiSelectFilterComponents = {
  statuses: WorkflowsStatusFilter,
  workflow_types: WorkflowsTypeFilter,
  member_ids: MemberFilter,
  broker_ids: AssigneesFilter,
  brokerage_ids: BrokerageFilter,
  carrier_names: CarrierNameFilter,
  enrollment_categories: EnrollmentCategoryFilter,
  employer_ids: EmployerFilter,
  employer_review_statuses: EmployerReviewStatusFilter,
  enrollment_event_types: EnrollmentEventTypeFilter,
  coverage_types: CoverageTypeFilter,
  source: SourceFilter,
  workflow_task_types: WorkflowTaskTypeFilter,
  workflow_task_statuses: TasksStatusFilter,
  workflow_task_execution_methods: WorkflowTaskExecutionMethodFilter,
  workflow_task_execution_contexts: WorkflowTaskExecutionContextFilter,
  workflow_task_categories: WorkflowTaskCategoryFilter,
}

const DATE_GROUP = 'Date'
const WORKFLOW_GROUP = 'Workflow'
const ASSOCIATIONS_GROUP = 'Associations'
const TRIAGING_GROUP = 'Triaging'
const TASK_FILTERS_GROUP = 'Task Filters'

export const filterConfigurations: FilterConfigurations<
  WorkflowsFilters,
  WorkflowFilterGroupType
> = {
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: DATE_GROUP,
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: DATE_GROUP,
    isMultiValue: false,
  },
  deadline_date_after: {
    key: 'deadline_date_after',
    label: 'Deadline After',
    group: DATE_GROUP,
    isMultiValue: false,
  },
  deadline_date_before: {
    key: 'deadline_date_before',
    label: 'Deadline Before',
    group: DATE_GROUP,
    isMultiValue: false,
  },
  statuses: {
    key: 'statuses',
    label: 'Status',
    group: WORKFLOW_GROUP,
    isMultiValue: true,
  },
  workflow_types: {
    key: 'workflow_types',
    label: 'Type',
    group: WORKFLOW_GROUP,
    isMultiValue: true,
  },
  member_ids: {
    key: 'member_ids',
    label: 'Member',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  broker_ids: {
    key: 'broker_ids',
    label: 'Assignee',
    group: TRIAGING_GROUP,
    isMultiValue: true,
  },
  brokerage_ids: {
    key: 'brokerage_ids',
    label: 'Brokerage',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  carrier_names: {
    key: 'carrier_names',
    label: 'Carrier',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  enrollment_categories: {
    key: 'enrollment_categories',
    label: 'Enrollment Category',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  employer_ids: {
    key: 'employer_ids',
    label: 'Employer',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  employer_review_statuses: {
    key: 'employer_review_statuses',
    label: 'Employer Review Status',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  enrollment_event_types: {
    key: 'enrollment_event_types',
    label: 'Enrollment Event Type',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  coverage_types: {
    key: 'coverage_types',
    label: 'Coverage Type',
    group: ASSOCIATIONS_GROUP,
    isMultiValue: true,
  },
  source: {
    key: 'source',
    label: 'Source',
    group: WORKFLOW_GROUP,
    isMultiValue: true,
  },
  workflow_task_types: {
    key: 'workflow_task_types',
    label: 'Task Type',
    group: TASK_FILTERS_GROUP,
    isMultiValue: true,
  },
  workflow_task_statuses: {
    key: 'workflow_task_statuses',
    label: 'Task Status',
    group: TASK_FILTERS_GROUP,
    isMultiValue: true,
  },
  workflow_task_execution_methods: {
    key: 'workflow_task_execution_methods',
    label: 'Task Execution Method',
    group: TASK_FILTERS_GROUP,
    isMultiValue: true,
  },
  workflow_task_execution_contexts: {
    key: 'workflow_task_execution_contexts',
    label: 'Task Execution Context',
    group: TASK_FILTERS_GROUP,
    isMultiValue: true,
  },
  workflow_task_categories: {
    key: 'workflow_task_categories',
    label: 'Task Category',
    group: TASK_FILTERS_GROUP,
    isMultiValue: true,
  },
}

export const workflowsDefaultPinnedOptionsByFilterValue = {
  statuses: [],
  workflow_types: [],
  member_ids: [],
  broker_ids: [{ label: 'Unassigned', value: 'unassigned' }],
  brokerage_ids: [],
  carrier_names: [],
  enrollment_categories: [],
  employer_ids: [],
  employer_review_statuses: [],
  enrollment_event_types: [],
  coverage_types: [],
  source: [],
  workflow_task_types: [],
  workflow_task_statuses: [],
  workflow_task_execution_methods: [],
  workflow_task_execution_contexts: [],
  workflow_task_categories: [],
}

export const getWorkflowsFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case TRIAGING_GROUP:
      return 0
    case WORKFLOW_GROUP:
      return 1
    case TASK_FILTERS_GROUP:
      return 2
    case DATE_GROUP:
      return 4
    case ASSOCIATIONS_GROUP:
      return 5
    default:
      return 6
  }
}

type WorkflowsFiltersViewProps = {
  filters: WorkflowsFilters
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const WorkflowsFiltersView = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: WorkflowsFiltersViewProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={workflowsSingleSelectFilters}
      multiSelectFilters={workflowsMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={workflowsDefaultPinnedOptionsByFilterValue}
      getFiltersConfigurationSortValue={getWorkflowsFiltersConfigurationSortValue}
    />
  )
}

export default WorkflowsFiltersView
